import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  tableCellClasses,
  FormControl,
  Select,
  Menu,
  MenuItem,
  ListItemText,
  InputLabel,
  Tooltip,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  DialogActions,
  Switch,
  Divider,
} from "@mui/material";
import styled from "@emotion/styled";
import { makeStyles } from "@mui/styles";
import { FontFamily } from "assets";
import Colors from "assets/Style/Colors";
import { ErrorToaster, SuccessToaster } from "components/Toaster";
import { CircleLoading } from "components/Loaders";
import Pagination from "components/Pagination";
import { PrimaryButton } from "components/Buttons";
import { v4 as uuidv4 } from "uuid";
import InputField from "components/Input";
import { useForm } from "react-hook-form";
import moment from "moment";
import { CleanTypes, formatPermissionData, getFileSize } from "utils";
import SimpleDialog from "components/Dialog/SimpleDialog";
import SelectField from "components/Select";
import Uploading from "components/Uploading";
import instance from "config/axios";
import routes from "services/System/routes";
import UploadFile from "components/UploadFile";
import { useNavigate } from "react-router-dom";
import CustomerServices from "services/Customer";
import SystemServices from "services/System";
import UserServices from "services/User";
// import ExcelJS from "exceljs";
// import { saveAs } from "file-saver";
// import { PDFExport } from "@progress/kendo-react-pdf";
import { useAuth } from "context/UseContext";
import DropdownMenu from "components/DropDown";
import { Controller } from "react-hook-form";

// *For Table Style
const Row = styled(TableRow)(({ theme }) => ({
  border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    border: 0,
    textAlign: "center",
    whiteSpace: "nowrap",
    backgroundColor: Colors.primary,
    color: Colors.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    textWrap: "nowrap",
    padding: "5px !important",

    ".MuiBox-root": {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      justifyContent: "center",
      ".MuiBox-root": {
        cursor: "pointer",
      },
    },
    svg: {
      width: "auto",
      height: "24px",
    },
    ".MuiTypography-root": {
      textTransform: "capitalize",
      fontFamily: FontFamily.NunitoRegular,
      textWrap: "nowrap",
    },
    ".MuiButtonBase-root": {
      padding: "8px",
      width: "28px",
      height: "28px",
    },
  },
}));

const useStyles = makeStyles({
  loaderWrap: {
    display: "flex",
    height: 100,
    "& svg": {
      width: "40px !important",
      height: "40px !important",
    },
  },
  anchorLink: {
    textDecoration: "underline",
    color: Colors.twitter,
    cursor: "pointer",
  },
});

function GalaxyCustomers() {
  const classes = useStyles();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const { user } = useAuth();

  // Updated: Use user IDs for amin (56632) and anass (50928)
  const IMPORT_CUSTOMERS_GROUP_USER_IDS = [56197, 50917, 56743, 56632]; // 56632 = amin
  const IMPORT_CUSTOMERS_GROUP_RM_IDS = [50926, 56212, 50925, 56642, 50928]; // 50928 = anass
  const IMPORT_CUSTOMERS_GROUP_IDS = [
    ...IMPORT_CUSTOMERS_GROUP_USER_IDS,
    ...IMPORT_CUSTOMERS_GROUP_RM_IDS,
  ];

  const isImportCustomersGroupUser = (userId) =>
    IMPORT_CUSTOMERS_GROUP_IDS.includes(userId);

  // Only check by user ID for amin/anass
  const isImportCustomersSharedAccessUser = (currentUser) => {
    if (currentUser?.id === 56632) return true; // amin
    if (currentUser?.id === 50928) return true; // anass
    return isImportCustomersGroupUser(currentUser?.id);
  };

  const canAccessImportCustomersGroup = (currentUser, rmId) =>
    currentUser?.id === rmId ||
    isImportCustomersSharedAccessUser(currentUser) ||
    IMPORT_CUSTOMERS_GROUP_RM_IDS.includes(rmId);

  const getUniqueCustomers = (rows = []) => {
    const customersMap = new Map();

    rows.forEach((item) => {
      if (item?.id) {
        customersMap.set(item.id, item);
      }
    });

    return Array.from(customersMap.values());
  };

  const isUaeName = (name) => {
    const value = (name ?? "").trim().toLowerCase();
    return (
      value === "uae" ||
      value === "united arab emirates" ||
      value.includes("united arab emirates")
    );
  };

  const formatBranchDisplayName = (name) => {
    const raw = (name ?? "").trim();
    if (!raw) return "-";
    const base = raw.replace(/&\s*uae\s*$/i, "").trim();
    if (!base) return "-";
    return isUaeName(base) ? base : `${base}&UAE`;
  };
  const tableHead = [
    "Select",
    "Creator",
    "Customer Name",
    "Customer ID",
    "Email",
    "Nationality",
    "Customer Type",
    "Region",
    "Branch",
    "Action",
    "Whatsapp Notifications",
    "Email Notifications",
    "VCC Status",
    "Gate Pass Status",
    "Referee",
    "Referral Fee",
    "R.Manager",
    "Del.Referri",
  ];

  const [visibleColumns, setVisibleColumns] = useState([
    ...Array(tableHead?.length).keys(),
  ]);
  const [selectedRm, setSelectedRmId] = useState(null);

  const [totalDiscount, setTotalDiscount] = useState("0");
  const [creditDays, setCreditDays] = useState(15);

  const orderedFields = [
    "shipping_charges",
    "late_fee",
    "storage",
    "category_a",
    "broker_fee",
    "title_fee",
    "current_suv",
    "clearance_att_fee",
    "hybrid_fee",
    "heavy_equipment_fee",
    "parts_pickup_fee",
    "wrapping_fee",
    "relist_reversal_fee",
    "misc_fee",
    "total_discount$",
  ];

  const [activeFields, setActiveFields] = useState(
    Object.fromEntries(orderedFields.map((f) => [f, f === "total_discount$"])),
  );

  // const fieldPairs = [];
  // for (let i = 0; i < orderedFields.length; i += 2) {
  //     fieldPairs.push(orderedFields.slice(i, i + 2));
  // }
  const fieldTriplets = [];
  for (let i = 0; i < orderedFields.length; i += 3) {
    fieldTriplets.push(orderedFields.slice(i, i + 3));
  }

  const renderField = (key) => (
    <Controller
      key={key}
      name={key}
      control={control}
      defaultValue=""
      rules={{
        pattern: {
          value: /^\d+(\.\d{0,2})?$/,
          message: "Only numbers allowed, up to 2 decimals",
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <Box>
          {/* {key !== 'total_discount' && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!activeFields[key]}
                                    onChange={(e) =>
                                        setActiveFields({
                                            ...activeFields,
                                            [key]: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label={key.replace(/_/g, " ").toUpperCase()}
                        />
                    )} */}

          <InputField
            size="small"
            type="number"
            // label={key === 'total_discount' ? 'TOTAL DISCOUNT' : ''}
            value={field.value || ""}
            onInput={(e) => {
              const rawValue = e.target.value;
              const parsedValue = parseFloat(rawValue) || 0;

              field.onChange(rawValue);

              if (key !== "total_discount$") {
                const currentValues = {
                  ...watch(),
                  [key]: parsedValue,
                };

                const total = orderedFields
                  .filter((f) => f !== "total_discount$")
                  .reduce((sum, f) => {
                    const val = parseFloat(currentValues[f]) || 0;
                    return sum + val;
                  }, 0);

                setTotalDiscount(total.toFixed(2));

                setValue("total_discount$", total.toFixed(2), {
                  shouldValidate: false,
                  shouldDirty: true,
                });
              }
            }}
            error={error?.message}
            disabled={key === "total_discount$" || !activeFields[key]}
          />
        </Box>
      )}
    />
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
    control,
  } = useForm();

  const {
    register: register2,
    handleSubmit: handleSubmit2,
    formState: { errors: errors2 },
    setValue: setValue2,
    reset: reset2,
  } = useForm();
  const {
    register: register3,
    handleSubmit: handleSubmit3,
    formState: { errors: errors3 },
    reset: reset3,
  } = useForm();
  const {
    register: register4,
    handleSubmit: handleSubmit4,
    formState: { errors: errors4 },
    reset: reset4,
  } = useForm();
  const {
    register: register5,
    handleSubmit: handleSubmit5,
    formState: { errors: errors5 },
    reset: reset5,
    control: control5,
  } = useForm();
  const {
    register: register6,
    handleSubmit: handleSubmit6,
    formState: { errors: errors6 },
    reset: reset6,
    control: control6,
  } = useForm();
  const {
    register: register7,
    handleSubmit: handleSubmit7,
    formState: { errors: errors7 },
    reset: reset7,
    control: control7,
  } = useForm();
  const {
    register: register8,
    handleSubmit: handleSubmit8,
    formState: { errors: errors8 },
    reset: reset8,
    control: control8,
  } = useForm();
  const {
    register: register9,
    handleSubmit: handleSubmit9,
    formState: { errors: errors9 },
    reset: reset9,
    control: control9,
    getValues: getValues9,
    setValue: setValue9,
  } = useForm();
  const {
    register: register10,
    handleSubmit: handleSubmit10,
    formState: { errors: errors10 },
    reset: reset10,
    control: control10,
    getValues: getValues10,
    setValue: setValue10,
  } = useForm({
    mode: "onChange",
  });

  const {
    register: register11,
    handleSubmit: handleSubmit11,
    formState: { errors: errors11 },
    reset: reset11,
    control: control11,
    getValues: getValues11,
    setValue: setValue11,
  } = useForm();

  // *For Upload File types
  const allowFilesType = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "application/pdf",
  ];

  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  // *For Dialog Box
  const [vccDeclareDialog, setVccDeclareDialog] = useState(false);
  const [vccVatChargesDialog, setVccVatChargesDialog] = useState(false);

  // *For Vehicle List
  const [customerList, setCustomersList] = useState();
  const [customerReferral, setCustomersReferral] = useState();
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [vccDetail, setVccDetail] = useState();
  const [purposeDialog, setPurposeDialog] = useState(false);
  const [issueLoading, setIssueLoading] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null); // State to manage dropdown anchor
  // Open dropdown
  const handleOpenDropdown = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close dropdown
  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const [vccId, setVccId] = useState();

  // *For Booking Id
  const [bookingId, setBookingId] = useState();

  // *For Customer  Id
  const [customerId, setCustomerId] = useState();

  // *For Customer  phone
  const [customerPhone, setCustomerPhone] = useState();

  const [vin, setVin] = useState();
  // *For Dialog
  const [memoDialog, setMemoDialog] = useState(false);
  const [notificationDialog, setNotificationDialog] = useState(false);
  const [branchDialog, setBranchDialog] = useState(false);
  const [typeDialog, setTypeDialog] = useState(false);

  const [selectedVccPurpose, setSelectedVccPurpose] = useState(null);

  // *For Pagination
  const [totalCount, setTotalCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);
  const [limitPage, setLimitPage] = useState(1000);
  const [currentPage, setCurrentPage] = useState(1);

  // *For Uploaded Documents
  const [progress, setProgress] = useState(0);
  const [uploadedSize, setUploadedSize] = useState(0);
  const [documentDetail, setDocumentDetail] = useState([]);
  const [documentLink, setDocumentLink] = useState("");

  const [wholeCustomers, setWholeCustomers] = useState([]);

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const [declarationDialog, setDeclarationDialog] = useState(false);

  const [selectAll, setSelectAll] = useState(false);
  const [vccDeposit, setVccDeposit] = useState();

  // *For Filters
  const [filters, setFilters] = useState({});

  // *For Handle Date
  const [vccDate, setVccDate] = useState();
  const [vccExpDate, setVccExpDate] = useState();

  // *For Permissions
  const [permissions, setPermissions] = useState();
  const [selectedOption, setSelectedOption] = useState("Whatsapp");
  const [open, setOpen] = useState(false);
  const [openVccGp, setOpenVccGp] = useState(false);
  const [openConvertRegion, setOpenConvertRegion] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [bothEnabled, setBothEnabled] = useState(false);
  const [vccEnabled, setVccEnabled] = useState(false);
  const [regionEnabled, setRegionEnabled] = useState(false);
  const [timeDialog, setTimeDialog] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");
  const [discountDialog, setDiscountDialog] = useState(false);
  const [gpEnabled, setGPEnabled] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedReferrals, setSelectedReferrals] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [referralFee, setReferralFee] = useState("");
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);
  const [referralSearchOptions, setReferralSearchOptions] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [RMDialogOpen, setRMDialogOpen] = useState(false);

  const buttons = [
    {
      title: "Customer Disc",
      onClick: () => setDiscountDialog(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.customer_disc,
    },
    {
      title: "VCC/GP",
      onClick: () => setTimeDialog(true),
      disabled: selectedRm !== user?.id && selectedVehicles.length === 0,
    },
    {
      title: "Convert Region",
      onClick: () => setOpenConvertRegion(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.convert_region,
    },
    {
      title: "Disable VCC/GP",
      onClick: () => setOpenVccGp(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.disable_vcc_gp,
    },
    {
      title: "Disable Notifications",
      onClick: () => setOpen(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.disable_notification,
    },
    {
      title: "Customer Type",
      onClick: () => setTypeDialog(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.customer_type,
    },
    {
      title: "Branch",
      onClick: () => setBranchDialog(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.change_branch,
    },
    {
      title: "Notification",
      onClick: () => setNotificationDialog(true),
      disabled: selectedRm !== user?.id && selectedVehicles.length === 0,
    },
    {
      title: "Upload Memo",
      onClick: () => setMemoDialog(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.upload_memo,
    },
    {
      title: "Select Referral",
      onClick: () => setReferralDialogOpen(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.select_referral,
    },
    {
      title: "Update RM",
      onClick: () => setRMDialogOpen(true),
      disabled:
        (selectedRm !== user?.id && selectedVehicles.length === 0) ||
        !permissions?.disable_rm,
    },
  ];
  // *For Handle Date
  const handleVccDate = (newDate) => {
    try {
      // eslint-disable-next-line eqeqeq
      if (newDate == "Invalid Date") {
        setVccDate("invalid");
        return;
      }
      setVccDate(newDate);
      setValue("vccDate", newDate);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const [users, setUsers] = useState([]);

  const getUsers = async () => {
    setLoader(true);
    try {
      const limit = limitPage;
      let params = {
        limit,
      };
      const { data } = await UserServices.getUsers(params);

      const allUsers = data?.users?.rows || [];
      setUsers(allUsers);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const handleVccExpDate = (newDate) => {
    try {
      // eslint-disable-next-line eqeqeq
      if (newDate == "Invalid Date") {
        setVccExpDate("invalid");
        return;
      }
      setVccExpDate(newDate);
      setValue("vccExpDate", newDate);
    } catch (error) {
      ErrorToaster(error);
    }
  };
  // *For Get Vcc Vehicle List
  const getBranches = async () => {
    setLoader(true);
    try {
      const { data } = await SystemServices.getBranches();
      setBranches(data?.branches);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  // *For Get Vcc Vehicle List
  const getcustomerList = async (page, limit, filter) => {
    setLoader(true);
    try {
      const Page = page ? page : currentPage;
      const Limit = limit ? limit : pageLimit;
      const Filter = { ...filters, ...filter };
      setCurrentPage(Page);
      setPageLimit(Limit);
      setFilters(Filter);
      let params = {
        page: Page,
        limit: Limit,
      };
      params = { ...params, ...Filter };
      const shouldLoadSharedTeams =
        user?.role_id !== 1000 && isImportCustomersSharedAccessUser(user) && user?.id !== 56632 && user?.id !== 50928;

      // If amin (56632), show all customers (admin-like)
      if (user?.id === 56632) {
        const { data } = await CustomerServices.getGalaxyCustomerList({ ...params });
        setCustomersList(data?.customers?.rows);
        setTotalCount(data?.customers?.count);
      } else if (user?.id === 50928) {
        // anass: only see amin's customers
        const { data } = await CustomerServices.getGalaxyCustomerList({ ...params });
        const aminCustomers = (data?.customers?.rows || []).filter(c => c?.creator_id === 56632);
        setCustomersList(aminCustomers);
        setTotalCount(aminCustomers.length);
      } else if (shouldLoadSharedTeams) {
        const responses = await Promise.all(
          IMPORT_CUSTOMERS_GROUP_RM_IDS.map((rmId) =>
            CustomerServices.getGalaxyCustomerList({ ...params, rm_id: rmId }),
          ),
        );

        const mergedRows = getUniqueCustomers(
          responses.flatMap((response) => response?.data?.customers?.rows || []),
        );

        setCustomersList(mergedRows);
        setTotalCount(
          responses.reduce(
            (count, response) => count + (response?.data?.customers?.count || 0),
            0,
          ),
        );
      } else {
        const { data } = await CustomerServices.getGalaxyCustomerList(params);
        setCustomersList(data?.customers?.rows);
        setTotalCount(data?.customers?.count);
      }

      setSelectAll(false);
      // setPermissions(formatPermissionData(data?.permissions));
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const getcustomerReferral = async () => {
    setLoader(true);
    try {
      const { data } = await CustomerServices.getGalaxyCustomerReferral();
      setCustomersReferral(data?.customers?.rows);
      setPermissions(formatPermissionData(data?.permissions));
      setSelectAll(false);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const searchReferralCustomers = async (search) => {
    try {
      const params = {
        search,
        business_region: "import",
        page: 1,
        limit: 20,
      };
      const { data } = await CustomerServices.getGalaxyCustomerReferral(params);
      setReferralSearchOptions(
        (data?.customers?.rows || []).map((item) => ({
          id: item?.id,
          name: item?.name,
        })),
      );
    } catch (error) {
      ErrorToaster(error);
    }
  };

  useEffect(() => {
    searchReferralCustomers("");
  }, []);
  const disableNoti = async () => {
    try {
      let obj = {
        whatsapp_status: whatsappEnabled,
        email_status: emailEnabled,
        users: selectedVehicles,
      };
      const data = await CustomerServices.disableNotifications(obj);
      if (data?.status) {
        SuccessToaster(data?.message);
        setOpen(false);
        getcustomerList();
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };
  const disableVccGp = async () => {
    try {
      let obj = {
        vcc_status: vccEnabled,
        gp_status: gpEnabled,
        users: selectedVehicles,
      };
      const data = await CustomerServices.disableVccGP(obj);
      if (data?.status) {
        SuccessToaster(data?.message);
        setOpenVccGp(false);
        getcustomerList();
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };
  const allowVccGatePass = async () => {
    try {
      let obj = {
        customer_id: selectedVehicles[0],
        note: getValues9("notes"),
        valid_util: selectedTime?.value,
      };
      const data = await CustomerServices.allowVccGP(obj);
      if (data?.status) {
        SuccessToaster(data?.message);
        getcustomerList();
        handleCloseAllow();
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const userBookingDisc = async () => {
    try {
      const formValues = watch();
      const checkedKeys = Object.keys(activeFields).filter(
        (key) => activeFields[key] && key !== "total_discount$",
      );

      const obj = {
        users: selectedVehicles,
        discount: formValues?.total_discount$,
        credit_days: creditDays,
        fields: checkedKeys.reduce((acc, key) => {
          if (key !== "total_discount$") {
            acc[key] = {
              value: formValues[key] || "",
              checked: !!activeFields[key],
            };
          }
          return acc;
        }, {}),
      };

      const data = await CustomerServices.userBookingDisc(obj);
      if (data?.status) {
        SuccessToaster(data?.message);
        getcustomerList();
        handleCloseAllow();
        setDiscountDialog(false);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };
  const userReferralAmount = async () => {
    setIssueLoading(true);
    try {
      let obj = {
        users: selectedVehicles,
        reffree_amount: getValues11("Referal fee") ?? 0,
        reffree_id: selectedCustomer.id,
      };
      const data = await CustomerServices.userReferralAmount(obj);
      if (data?.status) {
        SuccessToaster(data?.message);
        getcustomerList();
        setReferralDialogOpen(false);
        handleCloseAllow();
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIssueLoading(false);
    }
  };

  const AddMangerRelationship = async () => {
    setIssueLoading(true);
    try {
      let obj = {
        users: selectedVehicles,
        rm_id: selectedManager.id,
      };
      const data = await CustomerServices.getRelationshipManager(obj);
      if (data?.status) {
        SuccessToaster(data?.message);
        getcustomerList();
        setRMDialogOpen(false);
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIssueLoading(false);
    }
  };

  const convertRegion = async () => {
    try {
      let obj = {
        customer_id: selectedVehicles[0],
        status: regionEnabled,
      };
      const data = await CustomerServices.convertRegion(obj);
      if (data?.status) {
        SuccessToaster(data?.message);
        setOpenConvertRegion(false);
        getcustomerList();
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };
  const handleAllCheck = (value) => {
    setSelectAll(!selectAll);

    if (value) {
      const filteredCustomerIds =
        user?.role_id === 1000
          ? customerList.map((data) => data.id) // ✅ Allow all if admin
          : customerList
              .filter(
                (data) =>
                  canAccessImportCustomersGroup(user, data?.rm_id),
              )
              .map((data) => data.id);

      setWholeCustomers([...selectedVehicles, ...filteredCustomerIds]);
      setSelectedVehicles(filteredCustomerIds);
    } else {
      const filteredArray = wholeCustomers.filter(
        (item) => !selectedVehicles.includes(item),
      );
      setSelectedVehicles(filteredArray);
    }
  };

  // *For Handle Filter
  const handleFilter = async (data) => {
    setLoader(true);
    if (isSearching) return;

    setIsSearching(true);
    try {
      await getcustomerList(1, "", data);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setIsSearching(false);
      setLoader(false);
    }
  };
  // *For Select and DeSelect Vehicles
  const handleCheck = (data) => {
    try {
      const isAdmin = user?.role_id === 1000;
      const isAllowedUser = canAccessImportCustomersGroup(user, data?.rm_id);
      const isAssignedRM = data?.rm_id === user?.id;

      if (!(isAdmin || isAllowedUser || isAssignedRM)) {
        ErrorToaster("You are not authorized to select this customer.");
        return;
      }

      const shallowCopy = [...selectedVehicles];
      const currentIndex = selectedVehicles.findIndex((e) => e === data?.id);

      if (currentIndex === -1) {
        shallowCopy.push(data?.id);
      } else {
        shallowCopy.splice(currentIndex, 1);
      }

      setWholeCustomers([...selectedVehicles, ...shallowCopy]);
      setSelectedVehicles(shallowCopy);
      setSelectedRmId(data?.rm_id);
      setWhatsappEnabled(data?.message_alert);
      setRegionEnabled(data?.customerProfile?.import_export);
      setEmailEnabled(data?.email_alert);

      const updatedReferrals = customerList.filter((customer) =>
        shallowCopy.includes(customer.id),
      );
      setReferrals(updatedReferrals);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Handle Close Vcc Dialog
  const handleCloseVccDialog = () => {
    reset();
    handleVccDate();
    handleVccExpDate();
  };

  const handleColumnChange = (event) => {
    const selectedColumns = event.target.value;
    // Sort the selected columns to maintain the correct order
    const sortedColumns = selectedColumns.sort((a, b) => a - b);
    setVisibleColumns(sortedColumns);
  };

  const handleMenuToggle = (menuId, isOpen) => {
    setActiveMenu(isOpen ? menuId : null);
  };

  const renderCellContent = (colIndex, item, isActive) => {
    const date = moment(item?.vcc_expiry_date).format("DD-MM-YYYY");
    const targetDate = moment(date, "DD-MM-YYYY");
    let daysRemaining = targetDate.diff(moment(), "days");
    if (daysRemaining < 0) {
      daysRemaining = 0;
    }
    switch (colIndex) {
      case 0:
        return (
          <Checkbox
            className="pdf-hide"
            disabled={
              item?.customerProfile?.customer_type_id == 4 ? true : false
            }
            checked={selectedVehicles.findIndex((e) => e === item?.id) !== -1}
            onChange={() => handleCheck(item)}
          />
        );
      case 1:
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={item?.creator ? item?.creator?.name : "Self"}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -2],
                      },
                    },
                  ],
                },
              }}
            >
              {/* {item?.name?.length > 17 ? item?.name?.slice(0, 11) + "..." : item?.name} */}
              <span>
                {item?.creator
                  ? item?.creator?.name.length > 17
                    ? item?.creator?.name?.slice(0, 11) + "..."
                    : item?.creator?.name
                  : "Self"}
              </span>
            </Tooltip>
            <Box
              component={"div"}
              sx={{ display: "none !important" }}
              className="pdf-show"
            >
              {item?.creator ? item?.creator?.name : "Self"}
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={item?.name ?? "-"}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -2],
                      },
                    },
                  ],
                },
              }}
            >
              {/* {item?.name?.length > 17 ? item?.name?.slice(0, 11) + "..." : item?.name} */}
              <span>{item?.name ?? "-"}</span>
            </Tooltip>
            <Box
              component={"div"}
              sx={{ display: "none !important" }}
              className="pdf-show"
            >
              {item?.name ?? "-"}
            </Box>
          </Box>
        );
      // item?.name ?? "-";
      case 3:
        return item?.id ?? "-";

      case 4:
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={item?.email ?? "-"}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -2],
                      },
                    },
                  ],
                },
              }}
            >
              {/* {item?.name?.length > 17 ? item?.name?.slice(0, 11) + "..." : item?.name} */}
              <span>
                {item?.email
                  ? item?.email.length > 20
                    ? item?.email?.slice(0, 20) + "..."
                    : item?.email
                  : "-"}
              </span>
            </Tooltip>
            <Box
              component={"div"}
              sx={{ display: "none !important" }}
              className="pdf-show"
            >
              {item?.email ?? "-"}
            </Box>
          </Box>
        );
      case 5:
        return item?.customerProfile?.nationality?.name ?? "-";
      case 6:
        return item?.customerProfile?.is_trusted
          ? "Trusted"
          : ("Regular" ?? "-");
      case 7:
        return item?.customerProfile?.import_export
          ? "Both"
          : (item?.business_region ?? "-");
      case 8:
        return formatBranchDisplayName(item?.branch?.name);
      case 9:
        return item?.booking?.vin ?? "-";
      case 10:
        return item?.message_alert ? "Disable" : "Enable";
      case 11:
        return item?.email_alert ? "Disable" : "Enable";
      case 13:
        return item?.vcc_alert ? "Disable" : "Enable";
      case 13:
        return item?.gate_pass_alert ? "Disable" : "Enable";
      case 14:
        return item?.referee ? item?.referee?.name : "-";
      case 15:
        return item?.reffree_amount ? item?.reffree_amount : 0;
      case 16:
        return item?.relationManager?.name ?? "-";
      case 17:
        return (
          <DropdownMenu
            item={item}
            // permissions={permissions}
            isOpen={activeMenu === item.id}
            onToggle={(isOpen) => handleMenuToggle(item.id, isOpen)}
            id={item?.id}
            navigate={navigate}
            deleteReferri
            onDeleteClick={() => {
              setOpenDialog(true);
              setSelectedCustomer(item.id);
            }}
          />
        );
      default:
        return "-";
    }
  };

  const handleUpload = async (file, docs) => {
    setProgress(0);
    try {
      const formData = new FormData();
      formData.append("document", file);
      const { data } = await instance.post(routes.uploadDocuments, formData, {
        onUploadProgress: (progressEvent) => {
          const uploadedBytes = progressEvent.loaded;
          const percentCompleted = Math.round(
            (uploadedBytes * 100) / progressEvent.total,
          );

          setProgress(percentCompleted);
          setUploadedSize(getFileSize(uploadedBytes));
        },
      });
      if (data) {
        docs[0].isUpload = true;
        docs[0].file = data?.data?.nations;
        setDocumentDetail(docs);
        setDocumentLink(data?.data?.nations);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Upload Document
  const handleUploadDocument = async (e) => {
    try {
      e.preventDefault();
      const file = e.target.files[0];
      let arr = [
        {
          id: uuidv4(),
          name: file?.name,
          file: "",
          type: file?.type.split("/")[1],
          size: getFileSize(file.size),
          isUpload: false,
        },
      ];
      if (allowFilesType.includes(file.type)) {
        setDocumentDetail(arr);
        handleUpload(file, arr);
      } else {
        ErrorToaster(`Only ${CleanTypes(allowFilesType)} formats is supported`);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };
  // *For Remove Uploaded Document
  const removeDoc = () => {
    try {
      setDocumentDetail([]);
      setDocumentLink("");
      setValue2("scanned", "");
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Create Make
  const createBranch = async (name) => {
    try {
      let obj = {
        name: name,
      };
      const { data } = await SystemServices.createBranch(obj);
      getBranches();
      setSelectedBranch(data?.branch);
      setValue("branch", data?.branch?.name);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Issue Vcc
  const CreateMemo = async (formData) => {
    setIssueLoading(true);
    try {
      let obj = {
        users: wholeCustomers,
        title: formData?.Title,
        document: documentLink,
      };

      const { message } = await CustomerServices.CreateMemo(obj);
      SuccessToaster(message);
      setMemoDialog(false);
      window.location.reload();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIssueLoading(false);
    }
  };
  // *For Issue Vcc
  const ChangeBranch = async (formData) => {
    setIssueLoading(true);
    try {
      let obj = {
        users: wholeCustomers,
        branch_id: selectedBranch?.id,
      };

      const { message } = await CustomerServices.ChangeBranch(obj);
      SuccessToaster(message);
      setMemoDialog(false);
      // window.location.reload()
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIssueLoading(false);
    }
  };

  // *For Issue Vcc
  const ChangeType = async (formData) => {
    setIssueLoading(true);
    try {
      let obj = {
        users: wholeCustomers,
        is_trusted: selectedType?.id,
      };

      const { message } = await CustomerServices.ChangeType(obj);
      SuccessToaster(message);
      setTypeDialog(false);
      // window.location.reload()
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIssueLoading(false);
    }
  };

  // *For Issue Vcc
  const CreateNotifcation = async (formData) => {
    setIssueLoading(true);
    try {
      let obj = {
        users: wholeCustomers,
        notificationTitle: formData?.Title,
        notificationBody: formData?.Body,
        document: documentLink,
      };

      const { message } = await CustomerServices.CreateNotifcation(obj);
      SuccessToaster(message);
      setMemoDialog(false);
      window.location.reload();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIssueLoading(false);
    }
  };

  const deleteReferri = async (userId) => {
    setIssueLoading(true);
    try {
      let obj = {
        user_id: userId,
      };

      const { data } = await CustomerServices.userReferriDelete(obj);
      setOpenDialog(false);
      getcustomerList();
      SuccessToaster(data?.message);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIssueLoading(false);
    }
  };
  // const downloadExcel = () => {
  //   const headers = tableHead.filter((item) => item !== "Action");
  //   const rows = customerList?.map((item) => [
  //     item?.name ?? "-",
  //     item?.id ?? "-",
  //     item?.uae_phone ?? "-",
  //     item?.email ?? "-",
  //     item?.customerProfile?.is_trusted ? "Trusted" : "Regular" ?? "-",
  //     item?.business_region ?? "-",
  //     item?.branch ? item?.branch?.name : "-",
  //     item?.booking?.vin ?? "-",
  //   ]);

  //   const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

  //   const buf = XLSX.write(wb, {
  //     bookType: "xlsx",
  //     type: "array",
  //     mimeType:
  //       "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   });

  //   saveAs(new Blob([buf]), "data.xlsx");
  // };
  useEffect(() => {
    getcustomerList();
    getBranches();
    getcustomerReferral();
    getUsers();
  }, []);
  useEffect(() => {
    if (selectedVehicles.length > 0) {
      const selectedCustomer = customerList.find(
        (c) => c.id === selectedVehicles[0],
      );
      if (selectedCustomer) {
        setVccEnabled(selectedCustomer.vcc_alert);
        setGPEnabled(selectedCustomer.gate_pass_alert);
      }
    }
  }, [selectedVehicles]);
  const handleClose = () => {
    setOpen(false);
  };
  const handleCloseVccGp = () => {
    setOpenVccGp(false);
  };
  const handleBothSwitch = (checked) => {
    setBothEnabled(checked);
    setWhatsappEnabled(checked);
    setEmailEnabled(checked);
  };
  const handleCloseConvertRegion = () => {
    setOpenConvertRegion(false);
  };
  const handleCloseAllow = () => {
    setTimeDialog(false);
    setSelectedTime("");
    setValue9("notes", "");
  };
  const handleCloseDiscount = () => {
    setDiscountDialog(false);
    setValue10("disciount", "");
  };

  const handleCloseRM = () => {
    setRMDialogOpen(false);
    setValue10("relManager", "");
  };
  const timer = [
    {
      id: 1,
      name: "30 Minutes",
      value: moment().add(30, "minutes").toISOString(),
    },
    { id: 2, name: "1 Hour", value: moment().add(1, "hour").toISOString() },
    { id: 3, name: "2 Hours", value: moment().add(2, "hours").toISOString() },
    { id: 4, name: "24 Hours", value: moment().add(24, "hours").toISOString() },
    { id: 5, name: "1 Month", value: moment().add(1, "month").toISOString() },
  ];
  return (
    <Box sx={{ m: 4 }}>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Typography
          sx={{
            p: 3,
            px: 5,
            fontSize: 17,
            fontFamily: FontFamily.openSans,
            fontWeight: "bold",
          }}
          id="alert-dialog-title"
        >
          {"Are You Sure You Want To Delete Referri?"}
        </Typography>

        <Grid item xs={12} sm={12} sx={{ mb: 2, textAlign: "center" }}>
          <PrimaryButton
            title="Yes"
            loading={issueLoading}
            type="button"
            buttonStyle={{
              minWidth: "120px",
              bgcolor: Colors.danger,
              "&:hover": { bgcolor: Colors.danger },
            }}
            onClick={() => {
              deleteReferri(selectedCustomer);
            }}
          />
          <Box component={"span"} sx={{ mx: 1 }} />
          <PrimaryButton
            title="No"
            type="button"
            buttonStyle={{ minWidth: "120px" }}
            onClick={() => setOpenDialog(false)}
          />
        </Grid>
      </Dialog>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle
          style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: 20,
            paddingInline: 150,
            paddingTop: 20,
          }}
        >
          Disable Notifications
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Grid item xs={12} container justifyContent="center">
              {/* Switch for WhatsApp */}
              <FormControlLabel
                control={
                  <Switch
                    checked={whatsappEnabled}
                    onChange={(e) => {
                      setWhatsappEnabled(e.target.checked);
                      if (!e.target.checked) setBothEnabled(false);
                    }}
                    color="primary"
                  />
                }
                label="WhatsApp"
              />

              {/* Switch for Email */}
              <FormControlLabel
                control={
                  <Switch
                    checked={emailEnabled}
                    onChange={(e) => {
                      setEmailEnabled(e.target.checked);
                      if (!e.target.checked) setBothEnabled(false);
                    }}
                    color="primary"
                  />
                }
                label="Email"
              />

              {/* Switch for Both */}
              <FormControlLabel
                control={
                  <Switch
                    checked={bothEnabled}
                    onChange={(e) => handleBothSwitch(e.target.checked)}
                    color="primary"
                  />
                }
                label="Both"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          style={{ justifyContent: "center", marginBottom: 20, gap: 10 }}
        >
          <PrimaryButton
            title="Cancel"
            onClick={handleClose}
            style={{
              width: "106px",
              height: "42px",
              background: "transparent",
              borderWidth: "1px",
              borderColor: "#0C6135",
              color: "#0C6135",
              border: "solid",
            }}
          />
          <PrimaryButton
            title="Confirm"
            onClick={disableNoti}
            style={{
              width: "106px",
              height: "42px",
              background: "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
            }}
          />
        </DialogActions>
      </Dialog>
      <Dialog open={openVccGp} onClose={handleCloseVccGp}>
        <DialogTitle
          style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: 20,
            paddingInline: 150,
            paddingTop: 20,
          }}
        >
          Disable VCC/GP
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Grid item xs={12} container justifyContent="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={vccEnabled}
                    onChange={(e) => {
                      setVccEnabled(e.target.checked);
                      if (!e.target.checked) setBothEnabled(false);
                    }}
                    color="primary"
                  />
                }
                label="VCC"
              />

              {/* Switch for Email */}
              <FormControlLabel
                control={
                  <Switch
                    checked={gpEnabled}
                    onChange={(e) => {
                      setGPEnabled(e.target.checked);
                      if (!e.target.checked) setBothEnabled(false);
                    }}
                    color="primary"
                  />
                }
                label="Gate Pass"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          style={{ justifyContent: "center", marginBottom: 20, gap: 10 }}
        >
          <PrimaryButton
            title="Cancel"
            onClick={handleCloseVccGp}
            style={{
              width: "106px",
              height: "42px",
              background: "transparent",
              borderWidth: "1px",
              borderColor: "#0C6135",
              color: "#0C6135",
              border: "solid",
            }}
          />
          <PrimaryButton
            title="Confirm"
            onClick={disableVccGp}
            style={{
              width: "106px",
              height: "42px",
              background: "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
            }}
          />
        </DialogActions>
      </Dialog>

      <Dialog open={openConvertRegion} onClose={handleCloseConvertRegion}>
        <DialogTitle
          style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: 20,
            paddingInline: 150,
            paddingTop: 20,
          }}
        >
          Convert Region
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Grid item xs={12} container justifyContent="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={regionEnabled}
                    onChange={(e) => {
                      setRegionEnabled(e.target.checked);
                    }}
                    color="primary"
                  />
                }
                label="Import To Export"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          style={{ justifyContent: "center", marginBottom: 20, gap: 10 }}
        >
          <PrimaryButton
            title="Cancel"
            onClick={handleCloseConvertRegion}
            style={{
              width: "106px",
              height: "42px",
              background: "transparent",
              borderWidth: "1px",
              borderColor: "#0C6135",
              color: "#0C6135",
              border: "solid",
            }}
          />
          <PrimaryButton
            title="Confirm"
            onClick={convertRegion}
            style={{
              width: "106px",
              height: "42px",
              background: "linear-gradient(90deg, #0C6135 0%, #5BBC0F 100%)",
            }}
          />
        </DialogActions>
      </Dialog>
      <SimpleDialog
        open={memoDialog}
        onClose={() => setMemoDialog(false)}
        title={"Upload Memo"}
      >
        <Box component="form" onSubmit={handleSubmit5(CreateMemo)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <InputField
                size={"small"}
                label={"Title"}
                placeholder={"Title"}
                error={errors5?.Title?.message}
                register={register5("Title", {
                  required: "Please enter Title.",
                })}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <Typography
                variant="body1"
                sx={{ color: Colors.charcoalGrey, mb: 1 }}
              >
                Upload Scanned Copy
              </Typography>
              <UploadFile
                Memo={true}
                style={{
                  backgroundColor: "#e7efeb",
                  width: "100%",
                  height: "200px",
                  display: "flex",
                  flexDirection: "column-reverse",
                  padding: "20px",
                  border: "1px dashed #aeb1b0",
                  borderRadius: "10px",
                }}
                accept={allowFilesType}
                register={register2("scanned", {
                  onChange: (e) => handleUploadDocument(e),
                })}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              {documentDetail.length > 0 && (
                <Typography
                  variant="body1"
                  sx={{ color: Colors.charcoalGrey, mb: 1 }}
                >
                  Uploaded Files
                </Typography>
              )}
              <Box sx={{ maxHeight: 300, overflow: "auto", pr: 1 }}>
                {documentDetail?.map((item, index) => (
                  <Uploading
                    key={index}
                    data={item}
                    uploadedSize={uploadedSize}
                    progress={progress}
                    removeDoc={() => removeDoc()}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} sx={{ mt: 2, textAlign: "center" }}>
              <PrimaryButton
                fullWidth={true}
                bgcolor={Colors.tableGradient}
                title="Upload"
                type="submit"
                loading={issueLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </SimpleDialog>
      <SimpleDialog
        open={timeDialog}
        onClose={() => handleCloseAllow()}
        title={"Unlock VCC & Gate Pass"}
      >
        <Box component="form" onSubmit={handleSubmit9(allowVccGatePass)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <SelectField
                size={"small"}
                label={"Allowed Untill"}
                options={timer}
                error={errors9?.allowed?.message}
                selected={selectedTime}
                onSelect={(value) => setSelectedTime(value)}
                register={register9("allowed", {
                  required: "Please enter Title.",
                })}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <InputField
                size={"small"}
                label={"Notes"}
                placeholder={"Notes"}
                error={errors9?.notes?.message}
                multiline={true}
                rows={4}
                register={register9("notes", {
                  required: "Please enter Body.",
                })}
              />
            </Grid>
            <Grid item xs={12} sm={12} sx={{ mt: 2, textAlign: "center" }}>
              <PrimaryButton
                fullWidth={true}
                title="Submit"
                type="submit"
                loading={issueLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </SimpleDialog>
      <SimpleDialog
        open={notificationDialog}
        onClose={() => setNotificationDialog(false)}
        title={"Send Notification"}
      >
        <Box component="form" onSubmit={handleSubmit6(CreateNotifcation)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <InputField
                size={"small"}
                label={"Title"}
                placeholder={"Title"}
                error={errors6?.Title?.message}
                register={register6("Title", {
                  required: "Please enter Title.",
                })}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <InputField
                size={"small"}
                label={"Body"}
                placeholder={"Body"}
                error={errors6?.Body?.message}
                multiline={true}
                rows={4}
                register={register6("Body", {
                  required: "Please enter Body.",
                })}
              />
            </Grid>

            <Grid item xs={12} sm={12} sx={{ mt: 2, textAlign: "center" }}>
              <PrimaryButton
                fullWidth={true}
                title="Submit"
                type="submit"
                loading={issueLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </SimpleDialog>
      <SimpleDialog
        open={branchDialog}
        onClose={() => setBranchDialog(false)}
        title={"Branch"}
      >
        <Box component="form" onSubmit={handleSubmit7(ChangeBranch)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <SelectField
                size={"small"}
                label={"Branch"}
                options={branches}
                addNew={(newValue) => createBranch(newValue)}
                selected={selectedBranch}
                onSelect={(value) => setSelectedBranch(value)}
                error={errors7?.branch?.message}
                register={register7("branch", {
                  required: "Please select  Branch.",
                })}
              />
            </Grid>

            <Grid item xs={12} sm={12} sx={{ mt: 2, textAlign: "center" }}>
              <PrimaryButton
                fullWidth={true}
                title="Submit"
                type="submit"
                loading={issueLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </SimpleDialog>
      <SimpleDialog
        open={typeDialog}
        onClose={() => setTypeDialog(false)}
        title={"Customer Type"}
      >
        <Box component="form" onSubmit={handleSubmit8(ChangeType)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <SelectField
                size={"small"}
                label={"Csutomer Type"}
                options={[
                  { id: true, name: "Trusted" },
                  { id: false, name: "Regular" },
                ]}
                selected={selectedType}
                onSelect={(value) => setSelectedType(value)}
                error={errors8?.type?.message}
                register={register8("type", {
                  required: "Please select type.",
                })}
              />
            </Grid>

            <Grid item xs={12} sm={12} sx={{ mt: 2, textAlign: "center" }}>
              <PrimaryButton
                fullWidth={true}
                title="Submit"
                type="submit"
                loading={issueLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </SimpleDialog>
      <SimpleDialog
        open={discountDialog}
        onClose={() => handleCloseDiscount(false)}
        title={`Customer Discount $`}
      >
        <Box component="form" onSubmit={handleSubmit10(userBookingDisc)}>
          <Grid container spacing={2} sx={{ marginTop: "2px" }}>
            {fieldTriplets.map((triplet, index) => (
              <Grid container key={index} spacing={2}>
                {triplet.map((field) => (
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    key={field}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <Box
                      sx={{
                        width: "120px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                          mb: 0.5,
                        }}
                      >
                        {field !== "total_discount$" && (
                          <Checkbox
                            checked={!!activeFields[field]}
                            onChange={(e) =>
                              setActiveFields((prev) => ({
                                ...prev,
                                [field]: e.target.checked,
                              }))
                            }
                            size="small"
                            sx={{ p: 0.5 }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            pt: "2px",
                          }}
                        >
                          {field.replace(/_/g, " ").toUpperCase()}
                        </Typography>
                      </Box>

                      {/* Input Field */}
                      {renderField(field)}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ))}

            <Divider
              sx={{
                my: 2,
                borderRightWidth: 2,
                borderColor: "black",
                width: "100%",
              }}
            />

            <Grid container spacing={1} alignItems="center">
              {/* Label */}
              <Grid item xs="auto">
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    fontFamily: FontFamily.NunitoRegular,
                  }}
                >
                  Credit Time To Customer
                </Typography>
              </Grid>

              {/* Input Field */}
              <Grid item xs={2}>
                <InputField
                  size="small"
                  type="number"
                  value={creditDays}
                  onInput={(e) => setCreditDays(Number(e.target.value))}
                  sx={{
                    width: "100px",
                    "& input": { padding: "6px", textAlign: "center" },
                  }}
                />
              </Grid>

              {/* Days Label */}
              <Grid item xs="auto">
                <Typography
                  variant="body1"
                  sx={{ fontFamily: FontFamily.NunitoRegular }}
                >
                  Days
                </Typography>
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ mt: 2, textAlign: "center" }}>
              <PrimaryButton
                fullWidth
                title="Submit"
                type="submit"
                loading={issueLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </SimpleDialog>

      <SimpleDialog
        open={RMDialogOpen}
        onClose={() => handleCloseRM(false)}
        title={`Update Relationship Manager`}
      >
        <Box component="form" onSubmit={handleSubmit10(AddMangerRelationship)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <SelectField
                size="small"
                label={"Relationship Manager"}
                options={users
                  ?.filter(
                    (item) => item?.department?.name === "Customer Service",
                  )
                  .map((item) => ({ id: item?.id, name: item?.name }))}
                selected={selectedManager}
                onSelect={(value) => {
                  setSelectedManager(value);
                }}
                error={errors?.relManager?.message}
                register={register("relManager", {
                  required: "please select Relationship Manager",
                })}
              />
            </Grid>
            <Grid item xs={12} sm={12} sx={{ mt: 2, textAlign: "center" }}>
              <PrimaryButton
                fullWidth={true}
                title="Submit"
                type="submit"
                loading={issueLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </SimpleDialog>
      <SimpleDialog
        open={referralDialogOpen}
        onClose={() => setReferralDialogOpen(false)}
        title="Select Referral"
      >
        <Box component="form" onSubmit={handleSubmit11(userReferralAmount)}>
          <DialogContent>
            <Grid container spacing={1}>
              {/* Select Customer */}
              <Grid item xs={12}>
                <SelectField
                  size="small"
                  label="Refered By"
                  options={referralSearchOptions}
                  onSearch={(value) => searchReferralCustomers(value)}
                  error={errors11?.selectCustomer?.message}
                  selected={selectedCustomer}
                  onSelect={(value) => setSelectedCustomer(value)}
                  register={register11("selectCustomer", {
                    required: "Please select Customer.",
                  })}
                />
              </Grid>

              {/* Selected Customer List */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Selected SubCustomers
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflowY: "auto",
                    maxHeight: "180px",
                  }}
                >
                  {referrals.map((customer, index) => (
                    <Box
                      key={customer.id}
                      sx={{
                        px: 2,
                        py: 1.2,
                        borderBottom:
                          index !== referrals.length - 1
                            ? "1px solid #eee"
                            : "none",
                        fontSize: "15px",
                        fontFamily: FontFamily.NunitoRegular,
                        color: "#333",
                      }}
                    >
                      {customer.name}
                    </Box>
                  ))}
                </Box>
              </Grid>

              {/* Referral Fee */}
              <Grid item xs={12}>
                <InputField
                  size="small"
                  label="Referal Fee $"
                  placeholder="Referal Fee"
                  type="number"
                  register={register11("Referal fee", {
                    required: "Please enter Referal Fee.",
                    onChange: (e) => {
                      let amount = e.target.value.replace(/,/g, "");

                      setValue11("Referal fee", amount);
                    },
                  })}

                  // register={register11("Referal fee")}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <PrimaryButton title="Save" type="submit" loading={issueLoading} />
          </DialogActions>
        </Box>
      </SimpleDialog>

      <Grid
        item
        // spacing={1}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Grid item xs={12} sm={3}>
          <Typography
            variant="h5"
            sx={{
              color: Colors.charcoalGrey,
              fontFamily: FontFamily.NunitoRegular,
              mb: 4,
              mt: 2,
            }}
          >
            Galaxy Customers
          </Typography>
        </Grid>
        <Grid
          item
          xs={9}
          sm={9}
          display={"flex"}
          justifyContent={"flex-end"}
          marginRight={"22px"}
          gap={1}
        >
          {/* <PrimaryButton
                        className="custom-Button"
                        // disabled={selectedVehicles.length > 0 ? false : true}
                        title="Select Referal"
                        type="button"
                        buttonStyle={{
                            justifyContent: "space-evenly",
                            backgroundColor: "white",
                            color: "#0c6135",
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            ':hover': {
                                color: 'white'
                            },
                            path: { fill: "#0c6135" },
                        }}
                        textColor="#0c6135"
                        bgcolor={'white'}
                        // startIcon={<NotificationIcon />}
                        onClick={() => setReferralDialogOpen(true)}
                    /> */}
          {/* <PrimaryButton
                        className="custom-Button"
                        disabled={selectedVehicles.length > 0 ? false : true}
                        title="VCC/GP"
                        type="button"
                        buttonStyle={{
                            justifyContent: "space-evenly",
                            backgroundColor: "white",
                            color: "#0c6135",
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            ':hover': {
                                color: 'white'
                            },
                            path: { fill: "#0c6135" },
                        }}
                        textColor="#0c6135"
                        bgcolor={'white'}
                        // startIcon={<NotificationIcon />}
                        onClick={() => setTimeDialog(true)}
                    />
                    <PrimaryButton
                        disabled={selectedVehicles?.length > 0 ? false : true}
                        title="Convert Region"
                        type="button"
                        buttonStyle={{
                            justifyContent: "space-evenly",
                            backgroundColor: "white",
                            color: "#0c6135",
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            ':hover': {
                                color: 'white'
                            },
                            path: { fill: "#0c6135" },
                        }}
                        textColor="#0c6135"
                        bgcolor={'white'}
                        onClick={() => setOpenConvertRegion(true)}
                    />

                    {permissions?.disable_vcc_gp && (
                        <PrimaryButton
                            disabled={selectedVehicles?.length > 0 ? false : true}
                            title="Disable VCC/GP"
                            type="button"
                            buttonStyle={{
                                justifyContent: "space-evenly",
                                backgroundColor: "white",
                                color: "#0c6135",
                                boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                                ':hover': {
                                    color: 'white'
                                },
                                path: { fill: "#0c6135" },
                            }}
                            textColor="#0c6135"
                            bgcolor={'white'}
                            onClick={() => setOpenVccGp(true)}
                        />
                    )}
                    <PrimaryButton
                        disabled={selectedVehicles?.length > 0 ? false : true}
                        title="Disable Notifications"
                        type="button"
                        buttonStyle={{
                            justifyContent: "space-evenly",
                            backgroundColor: "white",
                            color: "#0c6135",
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            ':hover': {
                                color: 'white'
                            },
                            path: { fill: "#0c6135" },
                        }}
                        textColor="#0c6135"
                        bgcolor={'white'}
                        onClick={() => setOpen(true)}
                    />
                    <PrimaryButton
                        disabled={selectedVehicles.length > 0 ? false : true}
                        title="Customer Type"
                        type="button"
                        buttonStyle={{
                            justifyContent: "space-evenly",
                            backgroundColor: "white",
                            color: "#0c6135",
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            ':hover': {
                                color: 'white'
                            },
                            path: { fill: "#0c6135" },
                        }}
                        textColor="#0c6135"
                        bgcolor={'white'}
                        onClick={() => setTypeDialog(true)}
                    />
                    <PrimaryButton
                        disabled={selectedVehicles.length > 0 ? false : true}
                        title="Branch"
                        type="button"
                        buttonStyle={{
                            justifyContent: "space-evenly",
                            backgroundColor: "white",
                            color: "#0c6135",
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            ':hover': {
                                color: 'white'
                            },
                            path: { fill: "#0c6135" },
                        }}
                        textColor="#0c6135"
                        bgcolor={'white'}
                        onClick={() => setBranchDialog(true)}
                    />
                    <PrimaryButton
                        className="custom-Button"
                        disabled={selectedVehicles.length > 0 ? false : true}
                        title="Notification"
                        type="button"
                        buttonStyle={{
                            justifyContent: "space-evenly",
                            backgroundColor: "white",
                            color: "#0c6135",
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            ':hover': {
                                color: 'white'
                            },
                            path: { fill: "#0c6135" },
                        }}
                        textColor="#0c6135"
                        bgcolor={'white'}
                        startIcon={<NotificationIcon />}
                        onClick={() => setNotificationDialog(true)}
                    />
                    <PrimaryButton
                        className="custom-Button"
                        disabled={selectedVehicles.length > 0 ? false : true}
                        title="Upload Memo"
                        type="button"
                        buttonStyle={{
                            justifyContent: "space-evenly",
                            backgroundColor: "white",
                            color: "#0c6135",
                            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                            ':hover': {
                                color: 'white'
                            },

                            path: { fill: "#0c6135" },

                        }}
                        textColor="#0c6135"
                        bgcolor={'white'}
                        startIcon={<UploadMemoIcon />}
                        onClick={() => setMemoDialog(true)}
                    /> */}
          {(user?.role_id === 1000 ||
            selectedRm === user?.id ||
            isImportCustomersSharedAccessUser(user) ||
            IMPORT_CUSTOMERS_GROUP_RM_IDS.includes(selectedRm)) && (
            <IconButton
              aria-label="more"
              aria-controls="dropdown-menu"
              aria-haspopup="true"
              onClick={handleOpenDropdown}
            >
              <PrimaryButton title="Edit" />
            </IconButton>
          )}
          {/* Dropdown Menu */}
          <Menu
            id="dropdown-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleCloseDropdown}
          >
            {buttons.map((button, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  button.onClick(); // Trigger the button's click handler
                  handleCloseDropdown(); // Close the dropdown
                }}
                disabled={button.disabled} // Disable if no vehicles are selected
              >
                {button.title}
              </MenuItem>
            ))}
          </Menu>
        </Grid>
      </Grid>

      {/* Filters */}
      <Grid
        container
        spacing={1}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Grid item xs={12} sm={3}>
          <InputField
            size={"small"}
            label={"Search"}
            placeholder={"Search"}
            register={register2("search", {
              onChange: (e) => handleFilter({ search: e.target.value }),
            })}
          />
        </Grid>
        {/* {customerList?.length > 0 && (
          <Box
            sx={{
              textAlign: "right",
              p: 4,
              display: "flex",
              gap: 2,
            }}
          >
            <PrimaryButton
              title="Download PDF"
              type="button"
              style={{ backgroundColor: Colors.bluishCyan }}
              onClick={() => handleExportWithComponent(contentRef)}
            />
            <PrimaryButton
              title={"Download Excel"}
              onClick={() => downloadExcel()}
            />
          </Box>
        )} */}
      </Grid>

      <Grid item md={11}>
        {customerList && (
          <Box>
            <Grid container mb={2}>
              <Grid item xs={5}>
                <FormControl>
                  <InputLabel>Columns</InputLabel>
                  <Select
                    size={"small"}
                    multiple
                    value={visibleColumns}
                    label={"Columns"}
                    onChange={handleColumnChange}
                    renderValue={() => "Show/Hide"}
                  >
                    {tableHead.map((column, index) => {
                      if (column !== "Select" && column !== "Status") {
                        return (
                          <MenuItem key={index} value={index}>
                            <Checkbox
                              checked={visibleColumns.includes(index)}
                            />
                            <ListItemText primary={column} />
                          </MenuItem>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {customerList && (
              <Fragment>
                {/* <PDFExport
                  ref={contentRef}
                  landscape={true}
                  paperSize="A4"
                  margin={5}
                  fileName="Galaxy Customers"
                > */}
                <Box className="pdf-show" sx={{ display: "none" }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: Colors.charcoalGrey,
                        fontFamily: FontFamily.NunitoRegular,
                        mb: 2,
                      }}
                    >
                      Galaxy Customers
                    </Typography>
                    <Box
                      sx={{
                        fontWeight: 400,
                        fontSize: "12px",
                        mt: 1.5,
                        color: Colors.charcoalGrey,
                      }}
                    >
                      <span>Date: &nbsp;&nbsp;</span>
                      {moment().format("MM-DD-YYYY")}
                    </Box>
                  </Box>
                </Box>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "0px 8px 18px 0px #9B9B9B1A",
                    borderRadius: 2,
                    maxHeight: "calc(100vh - 330px)",
                  }}
                  className="table-box"
                >
                  <Table stickyHeader sx={{ minWidth: 500 }}>
                    {/* Table Header */}
                    <TableHead>
                      <TableRow className="pdf-table">
                        {visibleColumns.map((index) => (
                          <Cell className="pdf-table" key={index}>
                            {tableHead[index] == "Select" ? (
                              <Checkbox
                                className="pdf-hide"
                                sx={{ color: "white !important" }}
                                checked={selectAll}
                                onChange={() => handleAllCheck(!selectAll)}
                              />
                            ) : (
                              tableHead[index]
                            )}
                          </Cell>
                        ))}
                      </TableRow>
                    </TableHead>

                    {/* Table Body */}
                    <TableBody>
                      {!loader ? (
                        customerList?.length > 0 ? (
                          <Fragment>
                            {customerList?.map((item, rowIndex) => {
                              const isActive = true;
                              return (
                                <Row
                                  key={rowIndex}
                                  sx={{
                                    bgcolor: rowIndex % 2 !== 0 && "#EEFBEE",
                                  }}
                                >
                                  {visibleColumns.map((colIndex) => (
                                    <Cell className="pdf-table" key={colIndex}>
                                      {renderCellContent(
                                        colIndex,
                                        item,
                                        isActive,
                                      )}
                                    </Cell>
                                  ))}
                                </Row>
                              );
                            })}
                          </Fragment>
                        ) : (
                          <Row>
                            <Cell
                              colSpan={tableHead.length + 1}
                              align="center"
                              sx={{ fontWeight: 600 }}
                            >
                              No Data Found
                            </Cell>
                          </Row>
                        )
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={visibleColumns?.length + 2}
                            align="center"
                            sx={{ fontWeight: 600 }}
                          >
                            <Box className={classes.loaderWrap}>
                              <CircularProgress />
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* </PDFExport> */}
                {/* ========== Pagination ========== */}
                <Pagination
                  currentPage={currentPage}
                  pageSize={pageLimit}
                  onPageSizeChange={(size) =>
                    getcustomerList(1, size.target.value)
                  }
                  tableCount={customerList?.length}
                  totalCount={totalCount}
                  onPageChange={(page) => getcustomerList(page, "")}
                />
              </Fragment>
            )}

            {loader && <CircleLoading />}
          </Box>
        )}
      </Grid>
    </Box>
  );
}

export default GalaxyCustomers;
